import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";

export function Auth() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { signUp, signIn } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    if (isSignUp && password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const { error } = isSignUp
        ? await signUp(email, password)
        : await signIn(email, password);

      if (error) {
        setError(error.message);
      }
    } catch (err) {
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-morning-sky flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-warm-card rounded-2xl warm-shadow-lg p-8 w-full max-w-md border border-warm backdrop-blur-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-warm-dark mb-2 animate-slide-in">
            Wake & Take
          </h1>
          <p
            className="text-gray-700 animate-slide-in"
            style={{ animationDelay: "200ms" }}
          >
            Never miss your supplements again
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="animate-slide-in" style={{ animationDelay: "400ms" }}>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-warm-dark mb-1"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 border border-warm rounded-lg focus:ring-2 focus:ring-warm-primary focus:border-warm-solid bg-warm-glass transition-all duration-200 hover:bg-warm-light"
              placeholder="you@example.com"
            />
          </div>

          <div className="animate-slide-in" style={{ animationDelay: "500ms" }}>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-warm-dark mb-1"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-3 border border-warm rounded-lg focus:ring-2 focus:ring-warm-primary focus:border-warm-solid bg-warm-glass transition-all duration-200 hover:bg-warm-light"
              placeholder="••••••••"
            />
          </div>

          {isSignUp && (
            <div
              className="animate-slide-in"
              style={{ animationDelay: "600ms" }}
            >
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-warm-dark mb-1"
              >
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full px-4 py-3 border border-warm rounded-lg focus:ring-2 focus:ring-warm-primary focus:border-warm-solid bg-warm-glass transition-all duration-200 hover:bg-warm-light"
                placeholder="••••••••"
              />
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm animate-shake">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-warm-primary text-white py-3 rounded-lg font-medium hover:bg-warm-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 warm-shadow hover:warm-glow active:scale-95 animate-slide-in"
            style={{ animationDelay: "700ms" }}
          >
            {loading ? "Processing..." : isSignUp ? "Sign Up" : "Sign In"}
          </button>
        </form>

        <div
          className="mt-6 text-center animate-slide-in"
          style={{ animationDelay: "800ms" }}
        >
          <button
            onClick={() => {
              setIsSignUp(!isSignUp);
              setError("");
            }}
            className="text-warm-secondary hover:text-warm-dark text-sm font-medium transition-colors duration-200"
          >
            {isSignUp
              ? "Already have an account? Sign in"
              : "Don't have an account? Sign up"}
          </button>
        </div>
      </div>
    </div>
  );
}
